import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Select from '@/components/ui/select';
import { useStore } from '@/lib/store-context';

interface StoreOpt { store: string; label: string; default?: boolean }

async function fetchStores(): Promise<StoreOpt[]> {
  const r = await fetch('/api/stores', { credentials: 'include' });
  if (!r.ok) return [];
  const j = (await r.json()) as { stores: StoreOpt[] };
  return j.stores ?? [];
}

export const ALL_STORES = 'all';

export function StoreSelector() {
  const { selected, setSelected } = useStore();
  const q = useQuery({ queryKey: ['stores'], queryFn: fetchStores, staleTime: 5 * 60_000 });

  // Auto-pin to the configured default the first time stores load — only if
  // the URL truly has no selection yet. Pages that don't yet aggregate
  // (bills, returns, salesmen, etc.) need a concrete store; the "All stores"
  // option above is opt-in for users who explicitly want the cross-store view.
  React.useEffect(() => {
    if (selected || !q.data?.length) return;
    const def = q.data.find((s) => s.default) ?? q.data[0];
    if (def) setSelected(def.store);
  }, [q.data, selected, setSelected]);

  const value = selected ?? (q.data?.find((s) => s.default)?.store ?? q.data?.[0]?.store ?? '');
  const hasMultiple = (q.data?.length ?? 0) > 1;

  return (
    <Select.Root value={value} onValueChange={(v) => setSelected(v)}>
      <Select.Trigger className="w-[180px]">
        <Select.Value placeholder="Select store" />
      </Select.Trigger>
      <Select.Content>
        {hasMultiple && (
          <Select.Item value={ALL_STORES}>All stores</Select.Item>
        )}
        {(q.data ?? []).map((s) => (
          <Select.Item key={s.store} value={s.store}>
            {s.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  );
}
